// Typed calls for every meetings route (BUILD 1 contract C-9, plus the
// PLAN-ADDENDUM edit-ledger and live-row routes). Written by WP-0b and frozen
// after the b1-contracts tag; the owning packages call these and never build
// meeting URLs themselves.
//
// JSON routes go through `companyRequest` (cookie on the web, bearer in the
// shells; errors throw `CompanyRequestError{status, code}`). The one binary
// route, the capture chunk PUT, uses `authHeaders()` directly.
import { CompanyRequestError, companyRequest } from '@/company/api';
import { apiUrl, authHeaders } from '@/lib/shell';
import type {
  CompanyMeeting,
  MeetingMemberNote,
  MeetingCapabilities,
  MeetingCaptureLimits,
  MeetingCaptureSource,
  MeetingCaptureState,
  MeetingLegacyLanguage,
  MeetingLivePage,
  MeetingNotesState,
  MeetingParticipant,
  MeetingEdits,
  MeetingRepairAction,
  MeetingRepairDecision,
  MeetingRowSpeakerInput,
  MeetingSegment,
  MeetingSpeakerChange,
  MeetingSpeaker,
  MeetingSpeakerTarget,
  MeetingVoiceIdentificationInput,
  MeetingVoiceReview,
  MeetingVoiceState,
} from './types';

const root = '/api/v1/company';
const meetingPath = (id: string, rest = '') => `${root}/meetings/${encodeURIComponent(id)}${rest}`;
const part = (value: string | number) => encodeURIComponent(String(value));

export type MeetingParticipantInput =
  | { kind: 'member'; userId: string }
  | { kind: 'guest'; name: string; email?: string };

/** POST meetings (import). `language` is the legacy field and is still accepted. */
export type MeetingImportInput = {
  fileId: string;
  title?: string;
  language?: MeetingLegacyLanguage;
  captureMethod: 'upload' | 'browser';
  participants: MeetingParticipantInput[];
  visibility?: 'company' | 'private';
};

/** POST meetings/recordings. Idempotent on `clientCaptureId`. */
export type MeetingRecordingInput = {
  clientCaptureId: string;
  /** 1..300 characters: the user's title, or the localized `defaultTitle{date}`. */
  title: string;
  titleIsDefault: boolean;
  voiceIdentification?: boolean;
  /**
   * Always [] from this client: nobody picks a spoken language, so the
   * server's company or deployment hints apply.
   */
  languages: string[];
  sources: MeetingCaptureSource[];
  participants: MeetingParticipantInput[];
  consentAttested: true;
};
export type MeetingTakeInput = {
  sources: MeetingCaptureSource[];
  sampleRate: 16000;
  startedAt: string;
  deviceId: string;
};
export type MeetingTakeEndInput = {
  /** -1 when the take captured no frame. */
  lastSeq: number;
  totalSamples: number;
  endedAt: string;
  reason: 'stop' | 'reload' | 'crash' | 'sleep' | 'recovered';
};
export type MeetingChunkAck = {
  take: number;
  chunk: number;
  frames: number;
  samples: number;
  /** The meeting was stopped elsewhere: drain and end the take. */
  stopRequested: boolean;
};

/** The capture chunk PUT failed. `retryAfterSec` comes from a 429's `retry-after`. */
export class MeetingChunkError extends CompanyRequestError {
  readonly retryAfterSec: number | null;
  constructor(status: number, code: string, retryAfterSec: number | null) {
    super(status, code);
    this.retryAfterSec = retryAfterSec;
  }
}

const retryAfter = (response: Response) => {
  const value = Number(response.headers.get('retry-after'));
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * PUT meetings/:id/capture/takes/:take/chunks/:chunk with one PCM1 chunk
 * (C-10). `sha256` is the lowercase hex digest of `bytes`. The same bytes
 * again are a no-op on the server, so a retry after a lost response is safe.
 */
export async function putMeetingChunk(
  id: string,
  take: number,
  chunk: number,
  bytes: Uint8Array | ArrayBuffer,
  sha256: string,
  init: { signal?: AbortSignal } = {},
): Promise<MeetingChunkAck> {
  const response = await fetch(
    apiUrl(meetingPath(id, `/capture/takes/${part(take)}/chunks/${part(chunk)}`)),
    {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        ...authHeaders(),
        'content-type': 'application/octet-stream',
        'x-poneva-content-sha256': sha256,
      },
      body: bytes as BodyInit,
      signal: init.signal,
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new MeetingChunkError(
      response.status,
      error.code ?? error.error ?? 'request_failed',
      retryAfter(response),
    );
  }
  return response.json();
}

export const meetingsApi = {
  listMemberNotes: (id: string, offset = 0) =>
    companyRequest<{ notes: MeetingMemberNote[]; nextOffset: number | null }>(
      meetingPath(id, `/member-notes?offset=${offset}`),
    ),
  addMemberNote: (
    id: string,
    input: { content: string; clientNoteId: string; fileName?: string },
  ) => companyRequest<{ note: MeetingMemberNote }>(meetingPath(id, '/member-notes'), 'POST', input),
  deleteMemberNote: (id: string, noteId: string) =>
    companyRequest<{ deleted: boolean }>(
      meetingPath(id, `/member-notes/${part(noteId)}`),
      'DELETE',
    ),
  // ── Existing routes (extended in BUILD 1) ─────────────────────────────────
  list: () =>
    companyRequest<{ meetings: CompanyMeeting[] }>(`${root}/meetings`).then((r) => r.meetings),
  get: (id: string) =>
    companyRequest<{ meeting: CompanyMeeting }>(meetingPath(id)).then((r) => r.meeting),
  /** Import an uploaded recording or transcript → 202. */
  create: (input: MeetingImportInput) =>
    companyRequest<{ meeting: CompanyMeeting }>(`${root}/meetings`, 'POST', input).then(
      (r) => r.meeting,
    ),
  /** 202. Retries the failed stage, including `sealing`. */
  retry: (id: string) =>
    companyRequest<{ meeting: CompanyMeeting }>(meetingPath(id, '/retry'), 'POST', {}).then(
      (r) => r.meeting,
    ),
  /** Text edits only; bumps the transcript and re-indexes the meeting. */
  updateSegment: (id: string, index: number, input: { displayText: string }) =>
    companyRequest<{ segment: MeetingSegment }>(
      meetingPath(id, `/segments/${part(index)}`),
      'PATCH',
      input,
    ).then((r) => r.segment),

  // ── Capture (WP-4) ────────────────────────────────────────────────────────
  capabilities: () => companyRequest<MeetingCapabilities>(`${root}/meetings/capabilities`),
  startRecording: (input: MeetingRecordingInput) =>
    companyRequest<{ meeting: CompanyMeeting; capture: { limits: MeetingCaptureLimits } }>(
      `${root}/meetings/recordings`,
      'POST',
      input,
    ),
  /** 409 `meeting_capture_active` | `meeting_capture_closed`. */
  putTake: (id: string, take: number, input: MeetingTakeInput) =>
    companyRequest<{ take: number; offsetMs: number }>(
      meetingPath(id, `/capture/takes/${part(take)}`),
      'PUT',
      input,
    ),
  putChunk: putMeetingChunk,
  endTake: (id: string, take: number, input: MeetingTakeEndInput) =>
    companyRequest<{ take: number }>(
      meetingPath(id, `/capture/takes/${part(take)}/end`),
      'POST',
      input,
    ),
  captureState: (id: string) => companyRequest<MeetingCaptureState>(meetingPath(id, '/capture')),
  /** 202; `pending` lists chunks the server still waits for. */
  stopCapture: (id: string) =>
    companyRequest<{
      meeting: CompanyMeeting;
      pending: { take: number; missingChunks: number[] }[];
    }>(meetingPath(id, '/capture/stop'), 'POST', {}),
  /** Only while capturing. */
  discardCapture: (id: string) =>
    companyRequest<{ status: 'discarded' }>(meetingPath(id, '/capture'), 'DELETE'),

  // ── Notes (WP-5) ──────────────────────────────────────────────────────────
  /** 202; no speech-to-text call. */
  regenerateNotes: (id: string) =>
    companyRequest<{ notes: MeetingNotesState }>(
      meetingPath(id, '/notes/regenerate'),
      'POST',
      {},
    ).then((r) => r.notes),

  // ── Speakers (WP-6) ───────────────────────────────────────────────────────
  /** One row with its words (list and detail reads omit words). */
  segment: (id: string, index: number) =>
    companyRequest<{ segment: MeetingSegment }>(meetingPath(id, `/segments/${part(index)}`)).then(
      (r) => r.segment,
    ),
  addParticipant: (id: string, input: MeetingParticipantInput) =>
    companyRequest<{ participant: MeetingParticipant }>(
      meetingPath(id, '/participants'),
      'POST',
      input,
    ).then((r) => r.participant),
  /** Renames one voice: every row with that label follows. */
  updateSpeaker: (id: string, label: string, input: MeetingSpeakerTarget) =>
    companyRequest<MeetingSpeakerChange>(
      meetingPath(id, `/speakers/${part(label)}`),
      'PATCH',
      input,
    ),
  mergeSpeaker: (id: string, label: string, into: string) =>
    companyRequest<MeetingSpeakerChange>(
      meetingPath(id, `/speakers/${part(label)}/merge`),
      'POST',
      { into },
    ),
  unmergeSpeaker: (id: string, label: string) =>
    companyRequest<MeetingSpeakerChange>(
      meetingPath(id, `/speakers/${part(label)}/unmerge`),
      'POST',
      {},
    ),
  /** `scope` is required: the server never infers a voice rename from a row. */
  setSegmentSpeaker: (id: string, index: number, input: MeetingRowSpeakerInput) =>
    companyRequest<MeetingSpeakerChange & { segment?: MeetingSegment; scope: 'row' | 'label' }>(
      meetingPath(id, `/segments/${part(index)}/speaker`),
      'POST',
      input,
    ),
  /** Needs word timings (409 `meeting_split_needs_words`). */
  splitSegment: (
    id: string,
    index: number,
    input: { atWord: number } & ({ participantId?: string } | { label?: string }),
  ) =>
    companyRequest<{ segments: [MeetingSegment, MeetingSegment]; notes: MeetingNotesState }>(
      meetingPath(id, `/segments/${part(index)}/split`),
      'POST',
      input,
    ),

  // ── Transcript repair (WP-8, PLAN-ADDENDUM A3) ────────────────────────────
  /** The edit ledger, the repair state and the quality report. */
  edits: (id: string) => companyRequest<MeetingEdits>(meetingPath(id, '/edits')),
  /** Accept or reject a suggestion, or undo an applied edit; the notes follow. */
  decideEdit: (id: string, editId: string, action: MeetingRepairAction) =>
    companyRequest<MeetingRepairDecision>(
      meetingPath(id, `/edits/${part(editId)}/${action}`),
      'POST',
      {},
    ),

  // ── Live rows (PLAN-ADDENDUM A4, PT-008) ──────────────────────────────────
  /** Provisional rows persisted from the live preview while recording. */
  liveSegments: (id: string, options: { cursor?: string | null; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (options.cursor) params.set('cursor', options.cursor);
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString() ? `?${params}` : '';
    return companyRequest<MeetingLivePage>(meetingPath(id, `/live${query}`));
  },

  // ── Voice pass (BUILD 2 WP2-2, C2-6; the meeting's editor only) ───────────
  /** Uncertain rows, lowest margin first; `limit` 1..100. */
  voiceReview: (id: string, limit?: number) =>
    companyRequest<MeetingVoiceReview>(
      meetingPath(id, `/voice/review${limit ? `?limit=${part(limit)}` : ''}`),
    ),
  /** 202. Runs the voice pass again. */
  rerunVoice: (id: string) =>
    companyRequest<{ voice: MeetingVoiceState }>(meetingPath(id, '/voice/rerun'), 'POST', {}).then(
      (r) => r.voice,
    ),
  /** The per-meeting "suggest names from voices" choice; off deletes open voiceprint suggestions. */
  setVoiceIdentification: (id: string, enabled: boolean) =>
    companyRequest<{ voice: MeetingVoiceState; speakers: MeetingSpeaker[] }>(
      meetingPath(id, '/voice/identification'),
      'PUT',
      { enabled } satisfies MeetingVoiceIdentificationInput,
    ),
  /** Applies the suggestion's merge or name in one step; the notes go stale. */
  acceptVoiceSuggestion: (id: string, label: string, suggestionId: string) =>
    companyRequest<MeetingSpeakerChange>(
      meetingPath(id, `/speakers/${part(label)}/voice-suggestions/${part(suggestionId)}/accept`),
      'POST',
      {},
    ),
  dismissVoiceSuggestion: (id: string, label: string, suggestionId: string) =>
    companyRequest<{ speakers: MeetingSpeaker[] }>(
      meetingPath(id, `/speakers/${part(label)}/voice-suggestions/${part(suggestionId)}/dismiss`),
      'POST',
      {},
    ),
  /** "Keep as is" for an uncertain row. */
  undoVoiceRecognition: (id: string, label: string, recognitionId: string) =>
    companyRequest<MeetingSpeakerChange>(
      meetingPath(id, `/speakers/${part(label)}/voice-recognition/${part(recognitionId)}/undo`),
      'POST',
      {},
    ),
  markVoiceReviewed: (id: string, index: number) =>
    companyRequest<{ segment: MeetingSegment }>(
      meetingPath(id, `/segments/${part(index)}/voice-reviewed`),
      'POST',
      {},
    ).then((r) => r.segment),
};
